import type { ComponentProps } from 'react'
import { Controller, type FieldPath, type FieldPathByValue, type FieldValues, useFormContext } from 'react-hook-form'
import { Box } from '../../../../components/Box.tsx'
import { Alert, FormLabel } from '../../../../components/generic.tsx'
import { I18n } from '../../../../components/I18n.tsx'
import { type Dayjs, dayjs } from '../../../../dayjs.ts'
import { ids } from '../../../../ids.ts'
import { DateTimeField } from './DateTimeField.tsx'

export const StartEndDateTimeFields = <TForm extends FieldValues>({
	names,
	timezone,
	precision,
	min,
	max,
	disabled,
}: {
	names: {
		start: FieldPathByValue<TForm, string>
		end: FieldPathByValue<TForm, string>
		// Shows the errors of the range as a whole, such as the end being before the start.
		errorPath: FieldPath<TForm>
		// Re-validated once both edges are set; defaults to `errorPath`.
		revalidatePath?: FieldPath<TForm>
	}
	timezone: string
	precision?: ComponentProps<typeof DateTimeField>['precision']
	min?: Dayjs
	max?: Dayjs
	disabled?: boolean
}) => {
	const { control, trigger, getValues } = useFormContext<TForm>()

	// Validate the range only once both edges are set, to keep untouched fields from erroring
	const onEdgeBlur = () => {
		if (getValues(names.start) && getValues(names.end)) {
			void trigger(names.revalidatePath ?? names.errorPath)
		}
	}

	return (
		<>
			{(['start', 'end'] as const).map((edge) => (
				<Controller
					key={edge}
					control={control}
					name={names[edge]}
					render={({ field, fieldState }) => (
						<Box data-ta={ids.reports.form.timeRange[edge]}>
							<FormLabel required>
								<I18n i18nKey={($) => (edge === 'start' ? $.reports.form.startDateTime : $.reports.form.endDateTime)} />
							</FormLabel>
							<DateTimeField
								value={field.value ? dayjs(field.value) : null}
								timezone={timezone}
								precision={precision}
								min={min}
								max={max}
								disabled={disabled}
								onChange={(value) => field.onChange(value.toISOString())}
								onBlur={() => {
									field.onBlur()
									onEdgeBlur()
								}}
								error={fieldState.error?.message}
							/>
						</Box>
					)}
				/>
			))}
			<Controller
				control={control}
				name={names.errorPath}
				// biome-ignore lint/complexity/noUselessFragments: `render` typing does not support returning `null`
				render={({ fieldState }) => (fieldState.error?.message ? <Alert severity='error'>{fieldState.error.message}</Alert> : <></>)}
			/>
		</>
	)
}
